/**
 * **Getwhin / Electron**
 *
 * @exports bs_282
 * @category engines
 */
function factory({ window, document, thn }) {

  const engine = {};

  engine._hasChengineObj = function () {
    // Check if common objects between R&R and BC exists
    return !!(window.chengine && window.chengine.api_response && window.chengine.api_response.data && window.chengine.options && window.chengine.api_response.data.hotel && window.chengine.api_response.data.hotel.units.length);
  };

  /**
     * @memberOf module:bs_282
     * @see {@link prestart}
     *
     * @param {object} ctx - The context object.
     */
  engine.boot = function boot() {

    /**
         * https://alanniaresorts.com/es/resorts/alannia-costa-blanca (UID -> 1033909)
         *
         *  Define and observer that checks changes in the loading element.
         *  If the loader is not visible, the page is fully loaded
         *
         *  Sometimes, the script will load before the page is fully loaded. This observer also fixes this.
         *
         *  If the new page if 'Rooms and Rates', we update the PW to ensure that works correctly (some times page_check is not enough)
         */

    const loader = document.querySelector('#engine-loading');

    if (!loader) {
      return;
    }

    const handler = thn.debounce(() => {
      if (!thn.isVisible(loader)) {
        thn.pageCheck();
        if (thn.data.pageName === 'Rooms and Rates') {
          thn.widgets.price.update();
        }
      }
    }, 900);

    thn.observeDom(loader, handler, {
      childList: false,
      subtree: false,
      attributes: true,
    });

    thn.shutdown(() => {
      handler.cancel();
      thn.stopObserveDom(loader, handler);
    });

  };

  /**
     * @memberOf module:bs_282
     * @this thn.widgets.price
     * @see {@link Price#observe_dom}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceObserveDom = function priceObserveDom(ctx) {

    const tabsSelector = document.querySelector('.ch-tab-units');

    if (tabsSelector && !thn.guestproTabsObserver) {

      thn.guestproTabsObserver = thn.debounce(() => ctx.widget.refresh(), 500);

      thn.observeDom(tabsSelector, thn.guestproTabsObserver, {
        childList: true,
        subtree: false,
        attributes: true,
      });

      thn.pageUnload(function () {
        thn.guestproTabsObserver.cancel();
        thn.stopObserveDom(tabsSelector, thn.guestproTabsObserver);
        delete thn.guestproTabsObserver;
      });
    }
  };

  /**
     * @memberOf module:bs_282
     * @this thn.widgets.price
     * @see {@link Price#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceParseData_v1 = function priceParseData_v1(ctx) {

    // Window object 'chengine' version

    const widgetData = {};

    // If we can't use the object from window, we will use a fallback (another version)
    if (!engine._hasChengineObj()) {
      this.logger.warn('---  Suitable window object couldn\'t be found  ---');
      return widgetData;
    }

    // Check if there's rooms avaiable
    if (!window.chengine.api_response.data.hotel.units[0].rooms) {
      this.logger.warn('---  No rooms available  ---');
      widgetData.noRooms = true;
      return widgetData;
    }

    // Dates
    widgetData.dateBegin = thn.dateToString(window.chengine.api_response.data.start || '');
    widgetData.dateEnd = thn.dateToString(window.chengine.api_response.data.end || '');

    /**
         * When the user selects multiple rooms, in the array 'window.chengine.api_response.data.hotel.units' will appear more index, each one is the result
         * of the query that returns the rooms avaiables for each room (in multiroom case)
         */

    const searchResult = window.chengine.api_response.data.hotel.units[0];

    // Guests, Adults & Children
    widgetData.adults = searchResult.occupancy.adults || 1;
    widgetData.children = searchResult.occupancy.children || 0;
    widgetData.guests = widgetData.adults + widgetData.children;

    const numberChildrenInfant = widgetData.children + searchResult.occupancy.babies;

    // Rooms dispo
    const roomInfo = {
      adults: widgetData.adults,
      children: numberChildrenInfant,
      childrenAges: [],
    };

    for (let i = 1; i <= numberChildrenInfant || 0; i += 1) {
      // children: 2 - 11 years, babies: 0 - 2 years
      // thus standarize to safe values: 10 for children and 1 for babies
      roomInfo.childrenAges.push(i <= widgetData.children ? 10 : 0);
    }

    if (thn.empty(widgetData.roomsDispo)) {
      widgetData.roomsDispo = [];
    }
    widgetData.roomsDispo.push(roomInfo);

    // Multiple rooms case not needed
    widgetData.rooms = 1;

    // Prices & Currency || No Rooms
    /**
         * Obtain the object that contains the information of the room.
         *
         * We will obtain the cheapest room that is not a camping lot.
         *
         * Convert object collection to array -> exclude rooms by room_type_category_title -> obtain list of prices
         */
    const prices = Object.values(searchResult.rooms)
      .filter((room) => room.room_type_category_title.toLowerCase() !== 'parcelas')
      .map((room) => room.currency_lowest_amount_night);

    if (prices.length) {
      widgetData.price = Math.min(...prices);
      widgetData.currency = window.chengine.options.currency || 'EUR';
      widgetData.set = true;
    } else {
      widgetData.noRooms = true;
    }

    return widgetData;
  };

  /**
     * @memberOf module:bs_282
     * @this thn.widgets.price
     * @see {@link Price#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceParseData_v2 = function priceParseData_v2(ctx) {

    // HTML version

    const widgetData = {};

    // If the object 'chengine', don't use this fallback
    if (engine._hasChengineObj()) {
      this.logger.warn('---  Suitable window object found, not using this price_parse_data version (HTML)  ---');
      return widgetData;
    }

    // Dates
    const dateBegin = thn.parseUrlParameter('start_date');
    const dateEnd = thn.parseUrlParameter('end_date');
    if (dateBegin && dateEnd) {
      widgetData.dateBegin = dateBegin.replace(/-/g, '/');
      widgetData.dateEnd = dateEnd.replace(/-/g, '/');
      widgetData.nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);
    }

    // Guests
    const stringGuests = thn.parseUrlParameter('party');
    const actualRoom = thn.parseNumber(((document.querySelector('.ch-availability-results .uk-active') || {}).textContent || 1));
    const roomsGuests = stringGuests.split('-')[actualRoom - 1].split('.');
    widgetData.adults = thn.parseNumber(roomsGuests[0]);
    widgetData.children = roomsGuests.length - 1;

    // Currency
    widgetData.currency = thn.parseCurrency(((document.querySelector('.ch-currency a.active') || {}).textContent || 'EUR'));

    // Prices
    // If you select more than 1 rooms, create an ul with this li aria hidden, needed to get correct values for the selected room.
    // If you only have 1 room selected, this ul doesn't exist so get normal selector.
    // Used this way because in more than 1 room option both selectors exists.
    const selector = document.querySelector('#ch-availability-units>li[aria-hidden=false], .ch-search-results, .ch-availability-list');
    const prices = [...(selector?.querySelectorAll('.ch-availability-rate-label span.ch-text-green, .ch-text-green.ch-text-400.ch-cursor-default') || [])].map((el) => thn.parseNumber(el.textContent))
      .filter(Boolean);
    prices.sort((a, b) => a - b);

    if (!thn.empty(prices) && widgetData.nights) {
      widgetData.price = (prices[0] / widgetData.nights).toFixed(2);
      widgetData.set = true;
    } else {
      widgetData.noRooms = true;
    }

    return widgetData;
  };

  /**
     * @memberOf module:bs_282
     * @this thn.widgets.booking
     * @see {@link Booking#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.bookingParseData = function bookingParseData(ctx) {

    const widgetData = {};

    // Booking_ID
    const bookingData1 = window.chengine;
    const bookingData2 = document.querySelector('#ch-new-booking') && JSON.parse(document.querySelector('#ch-new-booking').getAttribute('data-ch-booking'));
    const bookingFailed = !thn.isVisible(document.querySelector('.uk-alert-success')) || !!document.querySelector('#submit-payment-form');

    if ((!bookingData1 && !bookingData2) || bookingFailed) {
      widgetData.set = false;
      return widgetData;
    }

    // Booking ID
    widgetData.bookingId = thn.objectGet(bookingData1?.api_response?.data, 'id')
                            || bookingData2?.booking_id;

    // Dates
    widgetData.dateBegin = thn.objectGet(bookingData1?.api_response?.data?.rooms, 'start_date')?.replace(/-/g, '/')
                            || bookingData2?.start_date?.replace(/-/g, '/');
    widgetData.dateEnd = thn.objectGet(bookingData1?.api_response?.data?.rooms, 'end_date')?.replace(/-/g, '/')
                          || bookingData2?.end_date?.replace(/-/g, '/');

    // Price & Currency
    widgetData.price = bookingData1?.api_response?.data?.total_amount || bookingData2?.total;
    widgetData.currency = bookingData1?.options?.currency || bookingData2?.currency;

    return widgetData;
  };

  return engine;

}

export { factory };
